import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Agent, EnrichedReviewGate, ExecutionMode, ReviewMode, ReviewPolicy } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { sseUrl } from '../lib/sse';
import { useApp } from '../contexts/AppContext';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { MarkdownPreview } from '../components/ui/MarkdownPreview';

const FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'Needs review', value: 'human' },
  { label: 'AI reviewed', value: 'ai' },
  { label: 'With code', value: 'code' },
  { label: 'Auto', value: 'auto' },
  { label: 'Draft', value: 'draft' },
] as const;

type FilterValue = (typeof FILTERS)[number]['value'];

function timeAgo(iso: string | Date): string {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function diffCount(gate: EnrichedReviewGate): number {
  const evidence = gate.evidence as { diffs?: { diffs?: unknown[] } } | undefined;
  return evidence?.diffs?.diffs?.length ?? 0;
}

export function ReviewPage() {
  const { workspaceId, workspaces } = useApp();
  const workspace = workspaces.find((row) => row.id === workspaceId);
  const [gates, setGates] = useState<EnrichedReviewGate[]>([]);
  const [policies, setPolicies] = useState<ReviewPolicy[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [tab, setTab] = useState<'queue' | 'history'>('queue');
  const [reason, setReason] = useState<Record<string, string>>({});
  const [activeFilter, setActiveFilter] = useState<FilterValue>('all');
  const [labelKey, setLabelKey] = useState('');
  const [agentId, setAgentId] = useState('');
  const [mode, setMode] = useState<ExecutionMode>('assist');
  const [reviewMode, setReviewMode] = useState<ReviewMode>('human');
  const [threshold, setThreshold] = useState(0.7);

  function loadGates(next = tab) {
    const status = next === 'history' ? 'decided' : 'pending';
    void apiClient.listReviewGates(status).then(setGates).catch(() => setGates([]));
  }

  function loadPolicies() {
    if (!workspaceId) return;
    void apiClient.listReviewPolicies().then(setPolicies).catch(() => setPolicies([]));
  }

  useEffect(() => {
    void apiClient.listAgents().then((page) => setAgents(page.items)).catch(() => setAgents([]));
  }, []);

  useEffect(() => {
    loadGates();
    loadPolicies();
    let source: EventSource | undefined;
    let closed = false;
    void (async () => {
      if (typeof EventSource === 'undefined') return;
      const url = await sseUrl('/api/review/gates/events', { workspaceId });
      if (closed) return;
      source = new EventSource(url);
      source.addEventListener('gate-deltas', (event) => {
        if (tab === 'history') return;
        try {
          const payload = JSON.parse((event as MessageEvent).data) as { gates: EnrichedReviewGate[] };
          const tenantId = localStorage.getItem('orqforge.tenantId');
          setGates((current) => {
            const byId = new Map(current.map((gate) => [gate.id, gate]));
            for (const gate of payload.gates ?? []) {
              const row = gate as EnrichedReviewGate & { tenantId?: string };
              if (tenantId && row.tenantId && row.tenantId !== tenantId) continue;
              byId.set(gate.id, gate);
            }
            return [...byId.values()];
          });
        } catch {
          // Ignore malformed stream payloads.
        }
      });
    })();
    return () => {
      closed = true;
      source?.close();
    };
  }, [workspaceId, tab]);

  const counts = useMemo(() => ({
    all: gates.length,
    human: gates.filter((gate) => gate.reviewerKind === 'human').length,
    ai: gates.filter((gate) => gate.reviewerKind === 'ai').length,
    code: gates.filter((gate) => diffCount(gate) > 0).length,
    auto: gates.filter((gate) => gate.executionMode === 'auto').length,
    draft: gates.filter((gate) => gate.executionMode === 'draft').length,
  }), [gates]);

  const filtered = gates.filter((gate) => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'human') return gate.reviewerKind === 'human';
    if (activeFilter === 'ai') return gate.reviewerKind === 'ai';
    if (activeFilter === 'code') return diffCount(gate) > 0;
    if (activeFilter === 'auto') return gate.executionMode === 'auto';
    if (activeFilter === 'draft') return gate.executionMode === 'draft';
    return true;
  });

  function nameOf(id?: string | null): string {
    if (!id) return 'any agent';
    return agents.find((agent) => agent.id === id)?.name ?? id.slice(0, 8);
  }

  async function decide(id: string, approve: boolean) {
    setError('');
    setSuccess('');
    setDecidingId(id);
    try {
      await apiClient.decideReview(id, approve, reason[id]);
      setReason((current) => ({ ...current, [id]: '' }));
      setSuccess(approve ? 'Approved. Outcome will be published.' : 'Changes requested. The run will continue.');
      loadGates();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit decision');
      loadGates();
    } finally {
      setDecidingId(null);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Review & Governance</h1>
          <p className="text-sm text-gray-500">Review agent outcomes for <strong>{workspace?.name || 'no workspace'}</strong>.</p>
        </div>
        <div className="flex overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
          <button type="button" className={`px-3 py-1 text-sm ${tab === 'queue' ? 'bg-blue-600 text-white' : ''}`} onClick={() => setTab('queue')}>Queue</button>
          <button type="button" className={`px-3 py-1 text-sm ${tab === 'history' ? 'bg-blue-600 text-white' : ''}`} onClick={() => setTab('history')}>History</button>
        </div>
      </div>
      {error && <div className="mt-4 rounded border border-red-300 bg-red-50 p-2 text-sm">{error}</div>}
      {success && <div className="mt-4 rounded border border-green-300 bg-green-50 p-2 text-sm">{success}</div>}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Filter:</span>
        {FILTERS.map((filter) => (
          <Button key={filter.value} size="sm" variant={activeFilter === filter.value ? 'default' : 'ghost'} onClick={() => setActiveFilter(filter.value)}>
            {filter.label} ({counts[filter.value]})
          </Button>
        ))}
        <span className="ml-auto text-xs text-gray-400">{filtered.length} outcome(s) · {gates.length} total</span>
      </div>
      {gates.length === 0 && (
        <div className="mt-4 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <div className="font-medium">{tab === 'queue' ? 'All clear!' : 'No review history'}</div>
          <div className="text-sm text-gray-500">{tab === 'queue' ? 'No outcomes awaiting review.' : 'Decided gates will appear here.'}</div>
        </div>
      )}
      <div className="mt-3 flex flex-col gap-4">
        {filtered.map((gate) => (
          <div key={gate.id} className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
            <div className="flex flex-wrap items-center gap-2">
              <strong className="text-sm">{nameOf(gate.agentId)}</strong>
              <Badge variant={gate.executionMode === 'auto' ? 'success' : gate.executionMode === 'draft' ? 'destructive' : 'warning'}>{gate.executionMode}</Badge>
              <Badge variant={gate.reviewerKind === 'human' ? 'warning' : 'secondary'}>{gate.reviewerKind === 'ai' ? 'AI reviewed' : gate.reviewerKind === 'human' ? 'Needs review' : 'Info'}</Badge>
              <span className="text-xs text-gray-400">Confidence {Math.round(gate.confidence * 100)}%</span>
              <span className="text-xs text-gray-400">· {timeAgo(gate.run.createdAt)}</span>
              {gate.run.conversationId && <Link className="text-xs text-blue-600" to={`/collaborate?conversationId=${gate.run.conversationId}`}>View in session</Link>}
              <Link className="text-xs text-blue-600" to={`/runs?runId=${gate.runId}`}>View run</Link>
            </div>
            <div className="mt-3 rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-700">
              <span className="text-xs text-gray-400">Request: </span>
              <MarkdownPreview content={gate.run.input || '(no request text)'} />
            </div>
            <div className="mt-3">
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Agent summary</div>
              <div className="max-h-48 overflow-auto rounded bg-gray-100 p-3 text-sm dark:bg-gray-800">
                <MarkdownPreview content={(gate.output ?? gate.recommendedAction ?? '').trim() || '(no proposed outcome)'} />
              </div>
            </div>
            {diffCount(gate) > 0 && <div className="mt-2 text-xs text-gray-500">Code changes: {diffCount(gate)} file(s)</div>}
            {tab === 'history' ? (
              <div className="mt-3 rounded-lg border border-gray-200 p-3 text-xs dark:border-gray-700">
                <div>Status: {gate.status}</div>
                {gate.decidedBy && <div>Actor: {gate.decidedBy}</div>}
                {gate.reason && <div>Reason: {gate.reason}</div>}
              </div>
            ) : (
              <div className="mt-3 flex flex-col gap-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <textarea className="w-full rounded border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-800" rows={2} placeholder="Add a review note (optional)" value={reason[gate.id] ?? ''} onChange={(event) => setReason((current) => ({ ...current, [gate.id]: event.target.value }))} />
                <div className="flex flex-wrap gap-2">
                  <Button className="flex-1 bg-green-600 hover:bg-green-700" size="sm" data-testid="review-approve" disabled={decidingId === gate.id} onClick={() => void decide(gate.id, true)}>Approve & publish</Button>
                  <Button className="flex-1" variant="outline" size="sm" disabled={decidingId === gate.id} onClick={() => void decide(gate.id, false)}>Request changes</Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {workspaceId && (
        <>
          <h2 className="mt-8 text-sm font-semibold uppercase tracking-wide text-gray-500">Review policies</h2>
          <div className="mt-2 grid gap-4 lg:grid-cols-3">
            <div className="flex flex-col gap-2 lg:col-span-2">
              {policies.length === 0 && <div className="rounded border border-gray-200 p-3 text-sm dark:border-gray-700">No policies. Outcomes fall back to workspace default.</div>}
              {policies.map((policy) => (
                <div key={policy.id} className="flex items-center justify-between rounded bg-gray-50 px-3 py-2 text-sm dark:bg-gray-800">
                  <div className="flex items-center gap-2">
                    <Badge>{policy.executionMode}</Badge>
                    <Badge variant="outline">review: {policy.reviewMode}</Badge>
                    <span>{policy.labelKey || 'any label'} · {nameOf(policy.agentId)}</span>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => void apiClient.deleteReviewPolicy(policy.id).then(() => loadPolicies())}>Delete</Button>
                </div>
              ))}
            </div>
            <div className="h-fit rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
              <h3 className="text-sm font-semibold">New policy</h3>
              <input className="mt-2 w-full rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800" placeholder="label key (optional)" value={labelKey} onChange={(event) => setLabelKey(event.target.value)} />
              <label className="mt-2 block text-xs text-gray-500">Agent</label>
              <select className="w-full rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800" value={agentId} onChange={(event) => setAgentId(event.target.value)}>
                <option value="">Any agent</option>
                {agents.map((agent) => <option key={agent.id} value={agent.id}>{agent.name}</option>)}
              </select>
              <label className="mt-2 block text-xs text-gray-500">Execution mode</label>
              <select className="w-full rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800" value={mode} onChange={(event) => setMode(event.target.value as ExecutionMode)}>
                <option value="assist">Assist</option>
                <option value="draft">Draft</option>
                <option value="auto">Auto</option>
              </select>
              <label className="mt-2 block text-xs text-gray-500">Review mode</label>
              <select className="w-full rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800" value={reviewMode} onChange={(event) => setReviewMode(event.target.value as ReviewMode)}>
                <option value="none">None</option>
                <option value="ai">AI</option>
                <option value="human">Human</option>
              </select>
              <label className="mt-2 block text-xs text-gray-500">confidence threshold ({(threshold * 100).toFixed(0)}%)
                <input type="range" min="0" max="1" step="0.05" className="w-full" value={threshold} onChange={(event) => setThreshold(Number(event.target.value))} />
              </label>
              <Button className="mt-2" size="sm" onClick={() => {
                void apiClient.createReviewPolicy({
                  labelKey: labelKey.trim() || undefined,
                  agentId: agentId || undefined,
                  executionMode: mode,
                  reviewMode,
                  confidenceThreshold: Number(threshold),
                  requiredReviewers: [],
                }).then(() => {
                  setLabelKey('');
                  setAgentId('');
                  setMode('assist');
                  setReviewMode('human');
                  setThreshold(0.7);
                  loadPolicies();
                });
              }}>Add policy</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
